import {
  cloneElement,
  MouseEventHandler,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeItemProps } from '@mui/lab/TreeItem';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import RememberMeOutlinedIcon from '@mui/icons-material/RememberMeOutlined';
import type { SimpleTreeViewApiRef } from '@mui/x-tree-view/SimpleTreeView/SimpleTreeView.types';

import AgVersion from '../../../../components/AgVersion';
import AgTypography from '../../../Styles/AgTypography';
import { AgColor } from '../../../Styles/AgColor';

import { LOGO_IMAGE_TYPE_ID } from '../../../../Constants';
import { getImage } from '../../../../redux/slices/organizations';
import { selectFullProfile } from '../../../../redux/slices/fullProfile';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import Oromia_Coffee_Logo from '../../../../assets/OCFCU.svg';
import {
  broadcast,
  certification,
  emergency,
  home,
  learning,
  logoWithText,
  member,
  message,
  organization,
  payment,
  report,
  staff,
  survey,
  wallet,
} from './icon';

interface BasicTreeItemProps extends TreeItemProps {
  label: string;
  icon?: ReactElement;
  needPerm?: number[];
  havePerm?: number[];
  messageNumber?: number;
}

export interface StyledTreeItemProps extends BasicTreeItemProps {
  primaryNode?: boolean;
}

export interface AdminTreeItemProps extends BasicTreeItemProps {
  admin?: boolean;
}

const treeItemStyle = {
  admin: {
    color: AgColor.grey1,
    fontFamily: 'Roboto',
    fontWeight: 500,
    fontSize: '10px',
    letterSpacing: '8%',
    lineHeight: '20px',
    gap: '8px',
  },
  adminItem: {
    color: 'inherit',
    fontFamily: 'Roboto',
    width: '150px',
    fontWeight: 400,
    fontSize: '12px',
    letterSpacing: '3%',
    lineHeight: '16px',
    gap: '8px',
  },
  primary: {
    color: 'inherit',
    fontFamily: 'Roboto',
    fontWeight: 500,
    width: '145px',
    fontSize: '10px',
    letterSpacing: '8%',
    lineHeight: '20px',
    gap: '8px',
  },
  secondary: {
    color: 'inherit',
    fontFamily: 'Inter',
    width: '160px',
    fontWeight: 500,
    fontSize: '12px',
    letterSpacing: '3%',
    lineHeight: '16px',
    gap: '8px',
  },
};

const renderStyledLabelIcon = (
  label: string,
  icon?: ReactElement,
  primaryNode?: boolean,
  messageNumber?: number
) => {
  return (
    <AgTypography
      style={{
        display: 'flex',
        alignItems: 'center',
        ...(primaryNode ? treeItemStyle.primary : treeItemStyle.secondary),
      }}
    >
      {icon &&
        cloneElement(icon, { style: { fontSize: '18px', width: '18px' } })}
      <FormattedMessage id={label} />
      {messageNumber && (
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Medium'}
          style={{
            width: '22px',
            minWidth: '22px',
            height: '20px',
            minHeight: '20px',
            borderRadius: '4px',
            color: AgColor.grey0,
            backgroundColor: AgColor.primaryHighlight,
            textAlign: 'center',
          }}
        >
          {messageNumber}
        </AgTypography>
      )}
    </AgTypography>
  );
};

const renderAdminLabelIcon = (
  label: string,
  icon?: ReactElement,
  admin?: boolean,
  messageNumber?: number
) => {
  return (
    <AgTypography
      style={{
        display: 'flex',
        alignItems: 'center',
        ...(admin ? treeItemStyle.admin : treeItemStyle.adminItem),
      }}
    >
      {icon &&
        cloneElement(icon, { style: { fontSize: '18px', width: '18px' } })}
      <AgTypography
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '160px',
          ...(admin ? treeItemStyle.admin : treeItemStyle.adminItem),
        }}
      >
        <FormattedMessage id={label} />
        {messageNumber && (
          <AgTypography
            contentStyle={'AP.H5.UPPERCASE.Medium'}
            style={{
              width: '22px',
              minWidth: '22px',
              height: '20px',
              minHeight: '20px',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            {messageNumber}
          </AgTypography>
        )}
      </AgTypography>
    </AgTypography>
  );
};

const hasPermission = (
  needPerm: number[] | undefined,
  havePerm: number[] | undefined
): boolean => {
  //at least one of the user's permission needs to be included in 'needPerm'
  if (!needPerm || !needPerm.length) return true;
  if (!havePerm?.length) return false;
  return havePerm.some((perm) => needPerm.includes(perm));
};

const StyledTreeItem = (props: StyledTreeItemProps) => {
  const {
    label,
    nodeId,
    primaryNode,
    icon,
    needPerm,
    havePerm,
    messageNumber,
    ...other
  } = props;
  if (!hasPermission(needPerm, havePerm)) return null;
  return (
    <TreeItem
      label={renderStyledLabelIcon(label, icon, primaryNode, messageNumber)}
      itemId={nodeId}
      sx={{
        marginTop: primaryNode ? '16px' : 0,
        '& .Mui-selected': {
          backgroundColor: `${AgColor.grey4} !important`,
        },
        '& .MuiTreeItem-root:hover': {
          backgroundColor: AgColor.grey2,
        },
        '& .MuiTreeItem-root': {
          '& .MuiTreeItem-content:hover': {
            backgroundColor: AgColor.grey2,
          },
        },
        '& .MuiTreeItem-content': {
          display: 'flex',
          gap: primaryNode ? '10px' : '8px',
          color: primaryNode
            ? `${AgColor.primaryHighlight}`
            : `${AgColor.primaryDark} !important`,
          height: primaryNode ? '34px' : '46px !important',
          alignItems: 'center',
          flexDirection: 'row-reverse',
          justifyContent: 'start',
          padding: '0 6px 0 24px',
        },
        '& .MuiTreeItem-label': {
          padding: '0 !important',
          ...(primaryNode ? treeItemStyle.primary : treeItemStyle.secondary),
        },
        '& .MuiTreeItem-iconContainer': {
          color: primaryNode ? AgColor.primaryHighlight : AgColor.primaryDark,
        },
        '& .MuiTreeItem-group': {
          margin: '0',
        },
      }}
      {...other}
    />
  );
};

const AdminTreeItem = (props: AdminTreeItemProps) => {
  const {
    label,
    nodeId,
    admin,
    icon,
    needPerm,
    havePerm,
    messageNumber,
    ...other
  } = props;
  if (!hasPermission(needPerm, havePerm)) return null;

  return (
    <TreeItem
      label={renderAdminLabelIcon(label, icon, admin, messageNumber)}
      itemId={nodeId}
      sx={{
        '& .MuiTreeItem-root': {
          backgroundColor: 'inherit',
        },
        '& .MuiTreeItem-content': {
          display: 'flex',
          gap: admin ? '10px' : '8px',
          height: admin ? '22px' : '46px !important',
          color: AgColor.grey1,
          alignItems: 'center',
          flexDirection: 'row-reverse',
          justifyContent: 'start',
          padding: admin ? '0 0 0 12px' : '0 0 0 10px',
          marginLeft: admin ? '0' : '4px',
          width: '169px',
          backgroundColor: 'inherit',
          '& .MuiTypography-root': {
            '& .MuiTypography-root': {
              '& .MuiTypography-root': {
                backgroundColor: AgColor.primaryLight2,
              },
            },
          },
        },
        '& .Mui-selected': {
          backgroundColor: admin ? AgColor.grey3 : AgColor.grey4,
          color: AgColor.primaryDark,
          '& .MuiTypography-root': {
            '& .MuiTypography-root': {
              '& .MuiTypography-root': {
                backgroundColor: AgColor.primaryDark,
                color: `${AgColor.grey0} !important`,
              },
            },
          },
        },
        '& .MuiTreeItem-content.Mui-selected': {
          backgroundColor: admin ? AgColor.primaryLight : AgColor.primaryLight2,
          borderRadius: '6px',
        },
        '& .MuiTreeItem-content:hover': {
          backgroundColor: admin
            ? `${AgColor.primaryLight} !important`
            : `${AgColor.primaryMain} !important`,
          color: AgColor.grey1,
          borderRadius: '6px',
        },
        '& .MuiTreeItem-label': {
          padding: '0 !important',
          ...(admin ? treeItemStyle.admin : treeItemStyle.adminItem),
        },
        '& .MuiTreeItem-iconContainer': {
          color: AgColor.grey1,
        },
        '& .MuiTreeItem-group': {
          margin: '0',
        },
      }}
      {...other}
    />
  );
};

type dataType = {
  id: string;
  name: string;
  sub?: dataType[];
  icon?: ReactElement | React.JSX.Element;
  hasNoPer?: boolean;
  expanded?: boolean;
  messageNumber?: number;
  onClick?: MouseEventHandler<HTMLLIElement>;
  disabled?: boolean;
  admin?: boolean;
};

const AgMainNavigation = ({
  example,
  data,
  apiRef,
}: {
  example?: boolean;
  data: dataType[];
  apiRef?: SimpleTreeViewApiRef;
}) => {
  const profile = useAppSelector(selectFullProfile);
  const dispatch = useAppDispatch();
  const imageId = profile?.imageId;
  const [imageURL, setImageURL] = useState<string>('');
  const year = new Date().getFullYear();

  useEffect(() => {
    const fetch = async () => {
      try {
        if (imageId !== undefined && imageId < 100) return;
        const response = await dispatch(
          getImage(LOGO_IMAGE_TYPE_ID, imageId || 0)
        );
        setImageURL(response?.data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    fetch();
  }, [dispatch, imageId]);

  return (
    <AgTypography
      style={{
        width: '215px',
        backgroundColor: AgColor.grey3,
        height: '100vh',
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        overflow: 'hidden',
      }}
    >
      <AgTypography>
        {imageId === 1 ? (
          <AgTypography
            style={{
              justifyContent: 'center',
              padding: '24px 24px 0px 24px',
              gap: '4px',
              marginBottom: '1rem',
            }}
          >
            <AgTypography
              style={{
                display: 'flex',
                margin: 0,
              }}
            >
              <img
                src={Oromia_Coffee_Logo}
                style={{
                  width: '167px',
                  height: '51px',
                  marginBottom: '8px',
                }}
              />
            </AgTypography>
            <AgTypography
              contentStyle={'AP.Assistive.Text.Regular'}
              style={{
                color: AgColor.primaryMain,
                textAlign: 'center',
                width: '167px',
              }}
            >
              Powered by AgUnity
            </AgTypography>
          </AgTypography>
        ) : imageId && imageId > 99 ? (
          <img
            src={
              imageURL?.includes('data:image')
                ? imageURL
                : 'data:image/png;base64,' + imageURL
            }
            style={{
              width: '167px',
              height: '51px',
              marginBottom: '8px',
            }}
          />
        ) : (
          <AgTypography
            style={{
              padding: '24px 24px 0 24px',
            }}
          >
            {cloneElement(logoWithText, { style: { marginBottom: '8px' } })}
          </AgTypography>
        )}
      </AgTypography>

      <AgTypography
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          scrollbarWidth: 'thin',
          scrollbarColor: `${AgColor.grey4} ${AgColor.grey3}`,
        }}
      >
        {!example ? (
          <SimpleTreeView
            apiRef={apiRef}
            aria-label='file system navigator'
            defaultExpandedItems={data.map((s) => (s.expanded ? s.id : ''))}
            sx={{
              '& .MuiTreeItem-root': {
                '& .MuiTreeItem-content:hover': {
                  backgroundColor: AgColor.grey2,
                },
              },
            }}
          >
            {data.map((item, index) => {
              return item.disabled ? null : item.admin ? (
                <AgTypography
                  key={index}
                  style={{
                    display: hasPermission([], []) ? 'flex' : 'none',
                    padding: '10px 4px 8px 4px',
                    justifyContent: 'start',
                    backgroundColor: AgColor.primaryLight,
                    margin: '20px 16px 0 16px',
                    borderRadius: '6px',
                  }}
                >
                  {item.sub ? (
                    <AdminTreeItem
                      disabled={item?.disabled}
                      key={index}
                      nodeId={item.id}
                      label={item.name.toUpperCase()}
                      admin
                      // primaryNode
                      onClick={item?.onClick}
                    >
                      {item?.sub?.map((s, index) => {
                        if (s.hasNoPer) return;
                        return (
                          <AdminTreeItem
                            disabled={s?.disabled}
                            onClick={s?.onClick}
                            key={index}
                            nodeId={s.id}
                            messageNumber={s?.messageNumber}
                            icon={s?.icon}
                            label={s.name}
                          />
                        );
                      })}
                    </AdminTreeItem>
                  ) : (
                    <AdminTreeItem
                      disabled={item?.disabled}
                      onClick={item?.onClick}
                      key={index}
                      nodeId={item.id}
                      messageNumber={item?.messageNumber}
                      icon={item?.icon}
                      label={item.name}
                    />
                  )}
                </AgTypography>
              ) : item.sub ? (
                <StyledTreeItem
                  disabled={item?.disabled}
                  key={index}
                  nodeId={item.id}
                  label={item.name.toUpperCase()}
                  primaryNode
                  onClick={item?.onClick}
                >
                  {item?.sub?.map((s, index) => {
                    if (s.hasNoPer) return;
                    return (
                      <StyledTreeItem
                        disabled={s?.disabled}
                        onClick={s?.onClick}
                        key={index}
                        nodeId={s.id}
                        messageNumber={s?.messageNumber}
                        icon={s?.icon}
                        label={s.name}
                      />
                    );
                  })}
                </StyledTreeItem>
              ) : (
                <StyledTreeItem
                  disabled={item?.disabled}
                  onClick={item?.onClick}
                  key={index}
                  nodeId={item.id}
                  messageNumber={item?.messageNumber}
                  icon={item?.icon}
                  label={item.name}
                />
              );
            })}
          </SimpleTreeView>
        ) : (
          <SimpleTreeView
            apiRef={apiRef}
            aria-label='file system navigator'
            defaultExpandedItems={['4', '5', '6']}
            sx={{
              '& .MuiTreeItem-root': {
                '& .MuiTreeItem-content:hover': {
                  backgroundColor: AgColor.grey2,
                },
              },
            }}
          >
            <StyledTreeItem
              nodeId='1'
              messageNumber={2}
              icon={home}
              label='SIDEBAR_HOME'
              needPerm={[]}
              havePerm={[]}
            />
            <StyledTreeItem
              nodeId='2'
              icon={message}
              label='SIDEBAR_MESSAGES'
              needPerm={[]}
              havePerm={[]}
            />
            <StyledTreeItem
              nodeId='3'
              messageNumber={1}
              icon={report}
              label='SIDEBAR_REPORTS'
              needPerm={[]}
              havePerm={[]}
            />
            <StyledTreeItem
              nodeId='4'
              label='SIDEBAR_TRANSACTIONS'
              primaryNode
              needPerm={[]}
              havePerm={[]}
            >
              <StyledTreeItem
                nodeId='11'
                icon={wallet}
                label='SIDEBAR_WALLET'
                needPerm={[]}
                havePerm={[]}
              />
              <StyledTreeItem
                nodeId='12'
                icon={payment}
                label='SIDEBAR_PAYMENT_MANAGEMENT'
                needPerm={[]}
                havePerm={[]}
              />
            </StyledTreeItem>
            <StyledTreeItem
              nodeId='5'
              label='SIDEBAR_CONTENT_MANAGEMENT'
              primaryNode
              needPerm={[]}
              havePerm={[]}
            >
              <StyledTreeItem
                nodeId='13'
                icon={broadcast}
                label='SIDEBAR_BROADCAST_MESSAGES'
                needPerm={[]}
                havePerm={[]}
              />
              <StyledTreeItem
                nodeId='14'
                icon={emergency}
                label='SIDEBAR_EMERGENCY_NUMBERS'
                needPerm={[]}
                havePerm={[]}
              />
              <StyledTreeItem
                nodeId='15'
                icon={learning}
                label='SIDEBAR_LEARNING_CONTENT'
                needPerm={[]}
                havePerm={[]}
              />
              <StyledTreeItem
                nodeId='16'
                icon={survey}
                label='SIDEBAR_SURVEYS'
                needPerm={[]}
                havePerm={[]}
              />
            </StyledTreeItem>
            <StyledTreeItem
              nodeId='6'
              label='SIDEBAR_DATA_MANAGEMENT'
              primaryNode
              needPerm={[]}
              havePerm={[]}
            >
              <StyledTreeItem
                nodeId='17'
                icon={member}
                label='SIDEBAR_MEMBERS'
                needPerm={[]}
                havePerm={[]}
              />
              <StyledTreeItem
                nodeId='18'
                icon={staff}
                label='SIDEBAR_STAFF'
                needPerm={[]}
                havePerm={[]}
              />
              <StyledTreeItem
                nodeId='19'
                icon={organization}
                label='SIDEBAR_ORGANIZATIONS'
                needPerm={[]}
                havePerm={[]}
              />
              <StyledTreeItem
                nodeId='20'
                icon={certification}
                label='SIDEBAR_CERTIFICATIONS'
                needPerm={[]}
                havePerm={[]}
              />
            </StyledTreeItem>
            <AgTypography
              style={{
                display: hasPermission([], []) ? 'flex' : 'none',
                padding: '10px 4px 8px 4px',
                justifyContent: 'start',
                backgroundColor: AgColor.primaryLight,
                margin: '20px 16px 0 16px',
                borderRadius: '6px',
              }}
            >
              <AdminTreeItem
                nodeId='7'
                messageNumber={8}
                label='SIDEBAR_ADMIN_FEATURES'
                admin
              >
                <AdminTreeItem
                  nodeId='101'
                  icon={<RememberMeOutlinedIcon />}
                  label='SIDEBAR_DEVICES_AND_ACCOUNTS'
                  needPerm={[]}
                  havePerm={[]}
                />
                <AdminTreeItem
                  nodeId='102'
                  messageNumber={2}
                  icon={<GroupOutlinedIcon />}
                  label='SIDEBAR_MENU_ITEM'
                  needPerm={[]}
                  havePerm={[]}
                />
                <AdminTreeItem
                  nodeId='103'
                  icon={<GroupOutlinedIcon />}
                  label='SIDEBAR_MENU_ITEM'
                  needPerm={[]}
                  havePerm={[]}
                />
                <AdminTreeItem
                  nodeId='104'
                  icon={<GroupOutlinedIcon />}
                  label='SIDEBAR_MENU_ITEM'
                  needPerm={[]}
                  havePerm={[]}
                />
                <AdminTreeItem
                  nodeId='105'
                  icon={<GroupOutlinedIcon />}
                  label='SIDEBAR_MENU_ITEM'
                  needPerm={[]}
                  havePerm={[]}
                />
              </AdminTreeItem>
            </AgTypography>
          </SimpleTreeView>
        )}
      </AgTypography>

      <AgTypography
        style={{
          fontFamily: 'Roboto',
          fontWeight: 400,
          fontSize: '9px',
          lineHeight: '16px',
          letterSpacing: '8%',
          color: AgColor.primaryHighlight,
          marginTop: '20px',
          padding: '0 24px 16px 24px',
          textTransform: 'uppercase',
        }}
      >
        <FormattedMessage id='SIDEBAR_COPYRIGHT' values={{ year }} />
        <AgVersion />
      </AgTypography>
    </AgTypography>
  );
};

export default AgMainNavigation;
